import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/context/CartContext";
import { useCity } from "@/context/CityContext";
import { useCustomer } from "@/context/CustomerContext";
import { api, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CreditCard, CalendarClock, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = ["Marriage", "Mehandi", "Reception", "Birthday", "Baby Shower", "Photography", "Other"];

const PLANS = [
  { key: "full", title: "Pay 100% now", discount: 10, payFraction: 1.0, note: "Best value — biggest discount" },
  { key: "half", title: "Pay 50% now", discount: 4, payFraction: 0.5, note: "Half now, rest on event day" },
  { key: "quarter", title: "Pay 25% now", discount: 2, payFraction: 0.25, note: "Small advance to lock your date" },
  { key: "on_event", title: "Pay in full on event day", discount: 0, payFraction: 0, note: "No advance, no discount" },
];

const round0 = (n) => Math.round(n);

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { city } = useCity();
  const { customer, loading: authLoading } = useCustomer();
  const [loading, setLoading] = useState("");
  const [plan, setPlan] = useState("full");
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    event_type: "Marriage", event_date: "", city: city || "Kundapura", notes: "",
  });

  const vendorIds = [...new Set(items.map((i) => i.vendor_id))];
  const { data: avail } = useQuery({
    queryKey: ["availability", vendorIds.join(",")],
    queryFn: async () => (await api.get(`/availability?vendor_ids=${vendorIds.join(",")}`)).data,
    enabled: vendorIds.length > 0,
  });

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items.length, navigate]);

  useEffect(() => {
    if (!authLoading && !customer) navigate("/login?next=/checkout");
  }, [authLoading, customer, navigate]);

  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.full_name || "",
        customer_phone: f.customer_phone || customer.mobile || "",
        customer_email: f.customer_email || customer.email || "",
      }));
    }
  }, [customer]);

  if (items.length === 0 || !customer) return null;

  const selected = PLANS.find((p) => p.key === plan);
  const finalTotal = round0(subtotal * (1 - selected.discount / 100));
  const payNow = round0(finalTotal * selected.payFraction);
  const balance = finalTotal - payNow;
  const isOnEvent = plan === "on_event";

  const conflictVendors = form.event_date && avail
    ? Object.values(avail).filter((v) => (v.blocked_dates || []).includes(form.event_date)).map((v) => v.name)
    : [];
  const hasConflict = conflictVendors.length > 0;

  const validate = () => {
    if (!form.customer_name || !form.customer_phone || !form.customer_email) {
      toast.error("Please fill your name, phone and email");
      return false;
    }
    if (hasConflict) {
      toast.error("Some vendors are unavailable on your event date. Please pick another date.");
      return false;
    }
    return true;
  };

  const createBooking = async (notify) => {
    const payload = {
      ...form,
      notify,
      payment_plan: plan,
      items: items.map((i) => ({
        service_id: i.service_id, vendor_id: i.vendor_id, vendor_name: i.vendor_name,
        name: i.name, price: i.price, unit: i.unit, quantity: i.quantity,
      })),
    };
    const { data } = await api.post("/bookings", payload);
    return data.id;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setLoading("pay");
    try {
      const bookingId = await createBooking(false);
      const { data } = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Payment could not be started. Try again.");
      setLoading("");
    }
  };

  const handleOnEvent = async () => {
    if (!validate()) return;
    setLoading("event");
    try {
      await createBooking(true);
      const vendorIds = [...new Set(items.map((i) => i.vendor_id))];
      await Promise.all(
        vendorIds.map((vid) =>
          api.post("/enquiries", {
            vendor_id: vid, customer_name: form.customer_name, phone: form.customer_phone,
            email: form.customer_email, event_type: form.event_type, event_date: form.event_date,
            message: form.notes || "Booking — pay in full on event day",
          })
        )
      );
      clear();
      toast.success("Booking confirmed! Pay in full on the event day. We'll call to confirm.");
      navigate("/account");
    } catch (e) {
      toast.error("Could not submit booking. Try again.");
      setLoading("");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 pb-28 grid md:grid-cols-5 gap-6">
      <div className="md:col-span-3">
        <h1 className="font-display font-extrabold text-3xl tracking-tight mb-5">Checkout</h1>
        <div className="grid gap-3">
          <Input data-testid="co-name" placeholder="Full name" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="co-phone" placeholder="Phone" value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            <Input data-testid="co-email" placeholder="Email" type="email" value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="co-event-type" value={form.event_type}
              onChange={(e) => setForm({ ...form, event_type: e.target.value })}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm">
              {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <Input data-testid="co-date" type="date" value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          </div>
          <Input data-testid="co-city" placeholder="City" value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Textarea data-testid="co-notes" placeholder="Any special requirements?" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          {hasConflict && (
            <div
              data-testid="date-conflict-warning"
              className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <b>{conflictVendors.join(", ")}</b> {conflictVendors.length > 1 ? "are" : "is"} already booked on{" "}
                {form.event_date}. Please choose a different event date to continue.
              </span>
            </div>
          )}
        </div>

        {/* Payment plan selector */}
        <h2 className="font-display font-bold text-xl tracking-tight mt-8 mb-3">Choose a payment plan</h2>
        <div className="grid gap-3" data-testid="payment-plans">
          {PLANS.map((p) => {
            const pFinal = round0(subtotal * (1 - p.discount / 100));
            const pPay = round0(pFinal * p.payFraction);
            const active = plan === p.key;
            return (
              <button
                key={p.key}
                data-testid={`plan-${p.key}`}
                onClick={() => setPlan(p.key)}
                className={`text-left rounded-2xl border p-4 transition-colors ${
                  active ? "border-primary bg-accent ring-2 ring-primary/30" : "border-stone-200 bg-card hover:border-stone-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-5 w-5 rounded-full border grid place-items-center shrink-0 ${active ? "bg-primary border-primary" : "border-stone-300"}`}>
                      {active && <Check className="h-3.5 w-3.5 text-white" />}
                    </div>
                    <div>
                      <p className="font-semibold flex items-center gap-2">
                        {p.title}
                        {p.discount > 0 && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                            {p.discount}% off
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.note}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">Final total</p>
                    <p className="font-display font-extrabold">{inr(pFinal)}</p>
                    <p className="text-[11px] text-primary font-semibold mt-0.5">
                      {p.payFraction === 0 ? "Pay on event day" : `Pay now ${inr(pPay)}`}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="md:col-span-2">
        <div className="bg-card border border-stone-200 rounded-2xl p-5 md:sticky md:top-24">
          <h2 className="font-display font-bold text-lg mb-3">Order summary</h2>
          <div className="grid gap-2 max-h-44 overflow-y-auto no-scrollbar">
            {items.map((i) => (
              <div key={i.service_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground truncate pr-2">{i.name} × {i.quantity}</span>
                <span className="font-semibold shrink-0">{inr(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-stone-200 mt-3 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">{inr(subtotal)}</span>
            </div>
            {selected.discount > 0 && (
              <div className="flex justify-between text-emerald-600 font-medium">
                <span>Discount ({selected.discount}%)</span>
                <span data-testid="checkout-discount">- {inr(subtotal - finalTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg pt-1">
              <span className="font-semibold">Final total</span>
              <span data-testid="checkout-final-total" className="font-display font-extrabold">{inr(finalTotal)}</span>
            </div>
            <div className="flex justify-between text-primary font-semibold">
              <span>{isOnEvent ? "Pay on event day" : "Pay now"}</span>
              <span data-testid="checkout-pay-now">{isOnEvent ? inr(finalTotal) : inr(payNow)}</span>
            </div>
            {!isOnEvent && balance > 0 && (
              <div className="flex justify-between text-amber-600 font-medium">
                <span>Balance on event day</span>
                <span data-testid="checkout-balance">{inr(balance)}</span>
              </div>
            )}
          </div>

          {isOnEvent ? (
            <button
              data-testid="confirm-onevent-btn"
              onClick={handleOnEvent}
              disabled={!!loading || hasConflict}
              className="mt-4 w-full h-12 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-primary/90 transition-colors"
            >
              <CalendarClock className="h-5 w-5" />
              {loading === "event" ? "Confirming..." : `Confirm — pay ${inr(finalTotal)} on event day`}
            </button>
          ) : (
            <button
              data-testid="pay-now-btn"
              onClick={handlePay}
              disabled={!!loading || hasConflict}
              className="mt-4 w-full h-12 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-primary/90 transition-colors"
            >
              <CreditCard className="h-5 w-5" />
              {loading === "pay" ? "Redirecting..." : `Pay ${inr(payNow)} now`}
            </button>
          )}
          <p className="text-xs text-muted-foreground mt-3 text-center">
            {isOnEvent
              ? "We'll confirm your booking and collect payment on the event day."
              : "Secure payment via Stripe. Test card 4242 4242 4242 4242."}
          </p>
        </div>
      </div>
    </div>
  );
}
